import { MenuItem } from 'components/navigation/KebabMenu';
import { DropdownMenuItem } from 'components/ui/dropdown-menu';
import { useMemo } from 'react';
import { useCanEditMatterQuery, useListEditableMattersQuery } from 'store/api/matters';
import { t } from 'utils/getTranslations';
import { isMatterInProgress } from 'utils/matterState';
import { useHasCloseCasePermission } from 'utils/useHasCloseCasePermission';
import { useCloseCaseModal } from './useCloseCaseModal';

type MatterStateCode = string | null | undefined;

export function isCloseCaseOffered(
    matterStateTypeCode: MatterStateCode,
    hasCloseCasePermission: boolean,
    canEditMatter: boolean
): boolean {
    return isMatterInProgress(matterStateTypeCode) && hasCloseCasePermission && canEditMatter;
}

/** Whether a row may offer "Close case" before its edit right is known: a row menu probes that when it opens. */
export function useMayOfferCloseCase(matterStateTypeCode: MatterStateCode): boolean {
    const hasCloseCasePermission = useHasCloseCasePermission();
    return isCloseCaseOffered(matterStateTypeCode, hasCloseCasePermission, true);
}

/**
 * The ids of the listed cases the user may close, for a list whose rows hold no edit right: one request answers
 * for every in-progress row, so a row whose only menu item would be "Close case" can hide its menu instead of
 * opening it empty.
 */
export function useClosableMatterIds(cases: { id: number; matterStateTypeCode: MatterStateCode }[]): Set<number> {
    const hasCloseCasePermission = useHasCloseCasePermission();
    const candidateIds = useMemo(
        () =>
            cases
                .filter((matter) => isCloseCaseOffered(matter.matterStateTypeCode, hasCloseCasePermission, true))
                .map((matter) => matter.id),
        [cases, hasCloseCasePermission]
    );
    const { data } = useListEditableMattersQuery(candidateIds, { skip: candidateIds.length === 0 });
    return useMemo(() => new Set(data ?? []), [data]);
}

export function useCloseCaseMenuItem(
    matter: { id: number; matterStateTypeCode: string; isEditable?: boolean },
    onClosed?: () => void
): MenuItem | null {
    const hasCloseCasePermission = useHasCloseCasePermission();
    const { openCloseCaseModal } = useCloseCaseModal();

    if (!isCloseCaseOffered(matter.matterStateTypeCode, hasCloseCasePermission, !!matter.isEditable)) {
        return null;
    }
    return {
        title: t('case-closing-entry-points.close-case'),
        handler: () => openCloseCaseModal({ matterId: matter.id, onClosed: () => onClosed?.() }),
    };
}

interface CloseCaseDropdownItemProps {
    matterId: number;
    matterStateTypeCode: MatterStateCode;
    /** The edit right when the caller already knows it; left out, the item asks for it when the menu opens. */
    canEditMatter?: boolean;
    onClosed?: () => void;
}

/**
 * List rows carry no edit right, so the item asks for it when the menu opens (the popup mounts its items only
 * then) and stays disabled until the answer arrives, so a menu holding only this item never opens empty.
 */
export function CloseCaseDropdownItem({
    matterId,
    matterStateTypeCode,
    canEditMatter,
    onClosed,
}: CloseCaseDropdownItemProps) {
    const mayOfferCloseCase = useMayOfferCloseCase(matterStateTypeCode);
    const { data: probedCanEditMatter, isFetching } = useCanEditMatterQuery(matterId, {
        skip: !mayOfferCloseCase || canEditMatter !== undefined,
    });
    const { openCloseCaseModal } = useCloseCaseModal();

    const isEditable = canEditMatter ?? probedCanEditMatter;
    if (!mayOfferCloseCase || (!isFetching && isEditable !== true)) {
        return null;
    }
    return (
        <DropdownMenuItem
            disabled={isFetching}
            onClick={() => openCloseCaseModal({ matterId, onClosed: () => onClosed?.() })}
        >
            {t('case-closing-entry-points.close-case')}
        </DropdownMenuItem>
    );
}
